import { For, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { getBackgroundImageSrcData, ImageSrcData } from '../image-component/image-component';
import theme, { breakpoints, fontFamilyAtosBold } from '../../style/theme';
import { Button } from '../../ui-components/button/button';
import { Heading } from '../../ui-components/heading/heading';
import { Text } from '../../ui-components/text/text';
import { HeroComponentProps } from './hero-types';
import { Grid } from '../../grid-system/grid/grid';
import { AngleDownIcon } from '../icons-library/angle-down';
import { focusPosition } from '../../tools/get-image-focus';
import { ErrorCatcher } from '../../tools/error-catcher';
import { ExpandableContainer } from '../../ui-components/expandable-container/expandable-container';
import { Link } from '../../ui-components/link/link';
import clsx from 'clsx';

const getOverlayClass = (overlayType?: string) => {
    if (overlayType === 'on') return 'hero-overlay-on';
    if (overlayType === 'auto') return 'hero-overlay-auto';
    return '';
};

export const Hero = (props: HeroComponentProps) => {
    const { imagesServiceUrl, supportedImageFormats, localize, viewport, siteInfo } = useContext(AppContext);

    const isMobile = () => viewport.width <= breakpoints.MOBILE;
    const isUnderDesktopLarge = () => viewport.width <= breakpoints.DESKTOP_LARGE;

    const imageWidth = () => {
        if (viewport.width <= breakpoints.DESKTOP) {
            return 1500;
        }
        return 1920;
    };

    const imageHeight = () => {
        if (viewport.width <= breakpoints.DESKTOP) {
            return 750;
        }
        return 960;
    };

    const getBackgroundImage = () => {
        if (props.src) {
            const backgroundImage: ImageSrcData = getBackgroundImageSrcData({
                imagesServiceUrl,
                supportedImageFormats,
                src: props.src,
                focus: props.focus,
                sign: props.sign,
                width: imageWidth() || 750,
                height: imageHeight() || 325,
            });
            return backgroundImage;
        }
    };

    const customCssRow = 'height: 100%;';

    const customCssSecondaryButton = `
        font-size: 1.29rem;
        letter-spacing: 0.3.2rem;
        line-height: 1.57rem;
        font-family: ${fontFamilyAtosBold};
        font-weight: 800;
        margin-left: 2rem;
    `;

    const handleScrollDown = (e: any) => {
        e.preventDefault();
        if (!window) {
            return;
        }

        window.scrollTo({
            top: window.innerHeight - (isMobile() ? 20 : 100),
            behavior: 'smooth'
        });
    };

    const withHeroMenu = () => props.showMenu && Boolean(props.menu);

    const bgImage = () => getBackgroundImage();

    const wrapperBgStyle = () => {
        const img = bgImage();
        if (img && img.original !== '') {
            return {
                'background-image': `url(${img.original})`,
                'background-repeat': 'no-repeat',
                'background-size': 'cover',
                'background-position': focusPosition(props.focus) || 'center',
            };
        }
        return {};
    };

    const mobileImageBgStyle = () => {
        const img = bgImage();
        if (img && img.original !== '') {
            return {
                'background-image': `url(${img.original})`,
                'background-position': focusPosition(props.focus) || 'center',
                'background-repeat': 'no-repeat',
                'background-size': 'cover',
            };
        }
        return {};
    };

    return (
        <ErrorCatcher componentName="Hero">
            <div class="relative">
                <div
                    class={clsx(
                        'hero-wrapper relative flex flex-col h-full min-h-screen',
                        withHeroMenu()
                            ? 'max-mobile:h-auto max-mobile:min-h-screen'
                            : 'max-mobile:h-screen max-mobile:min-h-[65vh]',
                        getOverlayClass(props.overlayType),
                    )}
                    style={wrapperBgStyle()}
                >
                    <Grid
                        templateShorthand={[12]}
                        isLayoutGrid={true}
                        customCss={`
                            height: 100%; ${isMobile() ? 'grid-template-rows: max-content;' : ''}
                        `}
                    >
                        <Grid templateShorthand={[1, 11]} responsive={{ tablet: [1, 11], mobile: [12] }} customCss={customCssRow}>
                            <div class="@max-[480px]:hidden" />
                            <div class="flex flex-col h-full z-[1] max-mobile:justify-start">
                                <div
                                    class={clsx(
                                        'block px-6 mt-[23vh] mb-0',
                                        'max-mobile:mt-[27%]',
                                        !withHeroMenu() && 'max-mobile:mb-[10%]',
                                        siteInfo?.siteType === 'atos-care' && 'mt-[32vh] max-mobile:mt-[calc(82px+27%)]',
                                    )}
                                >
                                    <div class="max-mobile:text-center max-mobile:mb-12">
                                        <Heading tag="h1" variant="heroEyebrow">
                                            {props.eyebrowHeading}
                                        </Heading>
                                        <div class="-translate-x-[5px]">
                                            <Heading tag="h2" variant="hero" noBlockSpacing={true}>
                                                {props.largerHeading}
                                            </Heading>
                                        </div>
                                    </div>
                                    <div class="my-[2.4rem]">
                                        <Text fontSize={isMobile() ? 'normal' : 'large'} displayRedVerticalLine={true}>
                                            {props.description}
                                        </Text>
                                    </div>
                                    <Show when={!withHeroMenu() && !isMobile()}>
                                        <div class="py-[2.14rem]">
                                            <Button label={localize('learn-more', 'Learn more')} onClick={handleScrollDown} />
                                        </div>
                                    </Show>
                                    <Show when={props?.customLink?.url}>
                                        <Button
                                            label={props.customLink.label ? props.customLink.label : ''}
                                            url={props.customLink.url}
                                            variant="tertiary"
                                            customCss={customCssSecondaryButton}
                                            isLink={true}
                                        />
                                    </Show>

                                    <Show when={withHeroMenu()}>
                                        <div class="flex gap-10 py-[2.14rem] max-[920px]:flex-col">
                                            <For each={props.menu}>{(menuItem, index) => (
                                                <Show when={menuItem.children && index() <= 2}>
                                                    <div
                                                        class={clsx(
                                                            'w-full min-w-[220px]',
                                                            'max-[1440px]:max-w-[270px] max-[1440px]:w-full',
                                                            'max-[920px]:max-w-[300px]',
                                                        )}
                                                        style={{ 'z-index': 2 - index() }}
                                                    >
                                                        <ExpandableContainer
                                                            enableOutsideClickToCloseOnDesktop={true}
                                                            isDropdown={true}
                                                            shrinkOnTablet={true}
                                                            backgroundColor={theme.palette.white}
                                                            chevronColor={theme.palette.darkGray}
                                                            chevronSize={isMobile() ? 1.8 : 2.2}
                                                            headingProps={{
                                                                children: menuItem.title,
                                                                tag: 'h4',
                                                                noBlockSpacing: true,
                                                                variant: isUnderDesktopLarge() ? 'tinyGray' : 'smallGray'
                                                            }}
                                                            customHeadingRowCss={`
                                                                padding-block: 2rem;
                                                                padding-inline: 0.5rem;
                                                            `}
                                                            customHeadingRowClass="hero-expandable-heading"
                                                        >
                                                            <ul class="border-t border-darkGray mx-2 py-[1.8rem]">
                                                                <For each={menuItem.children}>{(child, index) => (
                                                                    <Show when={index() <= 3}>
                                                                        <li class="py-[0.9rem]">
                                                                            <Link
                                                                                label={child.title}
                                                                                url={child.url}
                                                                                plainStyle={true}
                                                                                size={isMobile() ? 'small' : 'medium'}
                                                                                labelColor={theme.palette.darkGray}
                                                                                bold={true}
                                                                            />
                                                                        </li>
                                                                    </Show>
                                                                )}</For>
                                                            </ul>
                                                        </ExpandableContainer>
                                                    </div>
                                                </Show>

                                            )}</For>
                                        </div>
                                    </Show>

                                </div>
                            </div>
                        </Grid>
                        <div
                            class={clsx(
                                'hero-mobile-image hidden',
                                'max-mobile:flex max-mobile:grow max-mobile:justify-center max-mobile:mx-[-1.14rem]',
                                withHeroMenu() && 'max-mobile:h-[25rem]',
                            )}
                            style={mobileImageBgStyle()}
                        >
                            <Show when={!withHeroMenu()}>
                                <div id="learnMoreMobile" onClick={handleScrollDown}>
                                    <Button label={localize('learn-more', 'Learn more')} variant="tertiary" darkMode={true} />
                                    <div class="mt-[0.7rem]">
                                        <AngleDownIcon fill="white" ariaHidden />
                                    </div>
                                </div>
                            </Show>
                        </div>
                    </Grid>
                    <div class="mx-auto mt-auto mb-12 max-mobile:hidden [&_svg]:cursor-pointer [&_svg]:w-[2.14rem] [&_svg]:h-[2.14rem]">
                        <button
                            class="w-14 h-14 flex justify-center items-center border-none bg-transparent"
                            aria-label={localize('learn-more', 'Learn more')}
                            onClick={handleScrollDown}
                        >
                            <AngleDownIcon fill="white" ariaLabel={localize('learn-more', 'Learn more')} />
                        </button>
                    </div>
                </div>
            </div>
        </ErrorCatcher>
    );
};

Hero.parseProps = (atts: any) => {
    const { image, customLink } = atts;
    const commonAtts = {
        altText: atts.altText,
        imageText: atts.imageText,
        eyebrowHeading: atts.eyebrowHeading,
        largerHeading: atts.largerHeading,
        description: atts.description,
        labels: atts.labels,
        customLink: customLink ? customLink[0] : null,
        showMenu: atts.showMenu,
        menu: atts.menu,
    };

    if (image) {
        return {
            src: image.url,
            width: image.width,
            focus: image.focus,
            sign: image.sign,
            overlayType: atts.overlayType,
            ...commonAtts,
        };
    } else return commonAtts;
};
